/**
 * RodeoLevelConflictsPanel — rodeo-wide draw conflict rollup shown above
 * the per-event rows on the Manage Draws list.
 *
 * Why it exists: warnings used to be returned only by the per-event
 * /conflicts endpoint, which meant an admin had to drill into each event
 * just to learn one of them needed attention. Worse, sibling events'
 * warnings used to leak onto every per-event drilldown (see the
 * conflict-aggregator scoping fix). The Manage Draws list is the
 * scannable surface where the admin already lives, so the cross-event
 * rollup belongs here — one click per event that has work.
 */
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDrawConflictsForRodeo,
  type RodeoConflictReport,
} from '@/api/extensions/useDrawOps';

interface RodeoLevelConflictsPanelProps {
  rodeoId: string;
  /** Resolves an event_id to its display title so the per-event entries
   *  match what the row below the panel shows. Caller knows the events
   *  in the rodeo; we lean on them rather than re-fetching event names. */
  resolveEventTitle: (eventId: string) => string | undefined;
  onOpenEvent: (eventId: string) => void;
}

const SEVERITY_LABEL: Record<string, string> = {
  error: 'Error',
  warning: 'Warning',
};

export function RodeoLevelConflictsPanel({
  rodeoId,
  resolveEventTitle,
  onOpenEvent,
}: RodeoLevelConflictsPanelProps) {
  const { data, isLoading, error } = useDrawConflictsForRodeo(rodeoId);

  if (isLoading) {
    return <Skeleton className="h-16 w-full" />;
  }

  if (error || !data) {
    // Don't take real estate when this is just unavailable — the per-event
    // panel inside each draw still surfaces anything the admin needs.
    return null;
  }

  const report: RodeoConflictReport = data;
  const total = report.conflicts.length;

  if (total === 0) {
    return null;
  }

  // Group conflict messages by the event_id stamped in details. Conflicts
  // without an event_id (rare — only some buddy-group overlaps that can't
  // be tied to a registration) land in an "Unassigned" bucket so they
  // still show up.
  const byEvent = new Map<string, RodeoConflictReport['conflicts']>();
  const UNASSIGNED = '__unassigned__';
  for (const c of report.conflicts) {
    const eventId = (c.details?.event_id as string | null | undefined) ?? UNASSIGNED;
    const list = byEvent.get(eventId) ?? [];
    list.push(c);
    byEvent.set(eventId, list);
  }

  // Stable, deterministic ordering: events first (by descending count
  // then by title), unassigned bucket last.
  const eventRows = [...byEvent.entries()]
    .filter(([id]) => id !== UNASSIGNED)
    .map(([eventId, items]) => ({
      eventId,
      title: resolveEventTitle(eventId) ?? 'Untitled event',
      items,
    }))
    .sort((a, b) => b.items.length - a.items.length || a.title.localeCompare(b.title));
  const unassigned = byEvent.get(UNASSIGNED) ?? [];

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-500/5 dark:border-amber-900/60 p-3 text-sm">
      <div className="flex items-center gap-2 font-medium text-amber-900 dark:text-amber-200">
        <AlertTriangle className="h-4 w-4" />
        Rodeo draw conflicts ({total})
      </div>
      <p className="text-xs text-amber-800/80 dark:text-amber-300/70 mt-0.5">
        Click an event below to open its draw and resolve the conflict.
      </p>
      <ul className="mt-2 space-y-1.5">
        {eventRows.map(({ eventId, title, items }) => (
          <li
            key={eventId}
            className="rounded border border-amber-200 dark:border-amber-900/40 bg-white/60 dark:bg-amber-950/20 p-2"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-amber-900 dark:text-amber-100 truncate">
                  {title}
                </div>
                <ul className="mt-1 space-y-0.5">
                  {items.slice(0, 3).map((c, i) => (
                    <li
                      key={`${eventId}-${i}`}
                      className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300/90"
                    >
                      <span
                        className={`shrink-0 mt-0.5 text-[10px] uppercase tracking-wide font-semibold ${
                          c.severity === 'error'
                            ? 'text-red-700 dark:text-red-400'
                            : 'text-amber-700 dark:text-amber-400'
                        }`}
                      >
                        {SEVERITY_LABEL[c.severity] ?? c.severity}
                      </span>
                      <span>{c.message}</span>
                    </li>
                  ))}
                  {items.length > 3 && (
                    <li className="text-[11px] italic text-amber-700/80 dark:text-amber-400/70">
                      …and {items.length - 3} more
                    </li>
                  )}
                </ul>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 border-amber-400 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/40"
                onClick={() => onOpenEvent(eventId)}
              >
                Open draw
                <ChevronRight className="h-3 w-3 ml-1" />
                <Badge
                  variant="secondary"
                  className="ml-1 bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
                >
                  {items.length}
                </Badge>
              </Button>
            </div>
          </li>
        ))}
        {unassigned.length > 0 && (
          <li className="rounded border border-dashed border-amber-300 dark:border-amber-900/40 p-2 text-xs text-amber-800 dark:text-amber-300/80">
            <span className="font-medium">Rodeo-wide:</span>{' '}
            {unassigned.length} item{unassigned.length === 1 ? '' : 's'} not
            tied to a single event (e.g. cross-event buddy-group overlaps).
          </li>
        )}
      </ul>
    </div>
  );
}
